import { TaskCounter } from "@/components/TaskCounter";
import { HEADER_HEIGHT } from "@/config/data";
import { setIsPlayMode } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { getOrderedQaItems } from "@/utils/build-coverage-beta-qa-result";
import { isCoverageBetaReadOnlyRoute } from "@/utils/coverage-beta-read-only";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { ActionIcon, Badge, Button, Progress, Text } from "@mantine/core";
import { IconHelpCircle } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { CoverageBetaSkipBtn } from "./coverage-beta-skip-btn/CoverageBetaSkipBtn";
import { CoverageBetaSubmitBtn } from "./coverage-beta-submit-btn/CoverageBetaSubmitBtn";
import { CoverageBetaQaSubmitBtn } from "./coverage-beta-qa-submit-btn/CoverageBetaQaSubmitBtn";
import { CoverageQaEditActions } from "./coverage-qa-edit-actions/CoverageQaEditActions";

interface Props {
  taskDetailResp: JobTaskDetailSchema | undefined;
  /** Coverage Beta QA: swaps in the box-progress bar + QA submit button. */
  qaMode?: boolean;
  /** Editable whole-review Coverage QA: swaps in the QA actions. */
  qaEdit?: boolean;
}

/** "Reviewing box N of Total" — position of the active item among all four QA categories. */
const QaBoxProgress = () => {
  const idxToFeEmptyArea = useCoverageBetaStore((s) => s.idxToFeEmptyArea);
  const persons = useCoverageBetaStore((s) => s.persons);
  const activeQaItemKey = useCoverageBetaStore((s) => s.activeQaItemKey);
  const items = getOrderedQaItems(idxToFeEmptyArea, persons);
  const total = items.length;

  // A submission with no detections and nothing hand-drawn has no boxes to
  // page through (see CoverageBetaQaPanel's empty state) — say so instead of
  // leaving a blank gap next to the task counter.
  if (total === 0) {
    return (
      <div className="flex flex-col gap-0.5" style={{ minWidth: 140 }}>
        <Text size="xs" c="dimmed">
          No boxes to review
        </Text>
      </div>
    );
  }

  const position = items.findIndex((i) => i.key === activeQaItemKey) + 1;

  return (
    <div className="flex flex-col gap-0.5" style={{ minWidth: 140 }}>
      <Text size="xs" c="dimmed">
        Reviewing box {Math.max(position, 1)} of {total}
      </Text>
      <Progress value={(Math.max(position, 1) / total) * 100} size="xs" />
    </div>
  );
};

export const CoverageBetaHeader = (props: Props) => {
  const { taskDetailResp, qaMode, qaEdit } = props;
  const taskId = taskDetailResp?.id ?? "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Read-only routes (admin browse, QA feedback) render their own toolbar.
  const isReadOnlyView = isCoverageBetaReadOnlyRoute(useLocation().pathname);

  if (isReadOnlyView) return null;

  let exitTab = "coverage-beta";
  if (qaMode) exitTab = "coverage-beta-qa";
  else if (qaEdit) exitTab = "coverage-qa";

  let submitActions = <CoverageBetaSubmitBtn taskDetailResp={taskDetailResp} />;
  if (qaMode) {
    submitActions = <CoverageBetaQaSubmitBtn taskDetailResp={taskDetailResp} />;
  } else if (qaEdit) {
    submitActions = <CoverageQaEditActions taskDetailResp={taskDetailResp} />;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20 flex items-center justify-between px-4"
      style={{ height: HEADER_HEIGHT }}
    >
      <div className="flex items-center gap-2">
        <Text fw={600}>Out of Stock</Text>
        {/* Reopens the onboarding tutorial (see coverage-beta-training/).
            QA mode gets no tour — its footer/store shape are materially
            different from the labeler steps, so there's nothing to
            replay here. */}
        {qaEdit && (
          <Badge color="grape" variant="light">
            QA review
          </Badge>
        )}
        {!qaMode && !qaEdit && (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() =>
              useCoverageBetaStore.getState().setTutorialActive(true)
            }
            title="Show training walkthrough"
          >
            <IconHelpCircle size={18} />
          </ActionIcon>
        )}
      </div>

      <div
        className="flex items-center gap-2"
        data-training="top-bar-actions"
        data-cb-tour="top-bar-actions"
      >
        <TaskCounter taskId={taskId} />
        {qaMode && <QaBoxProgress />}
        <div className="w-px h-5 bg-gray-300" />
        {/* {isPlayMode && (
          <Button
            variant="subtle"
            color="red"
            size="sm"
            leftSection={<IconPlayerStopFilled size={16} />}
            onClick={() => {
              dispatch(setIsPlayMode(false));
              navigate("/?tab=coverage");
            }}
          >
            Play Mode
          </Button>
        )} */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            dispatch(setIsPlayMode(false));
            navigate(`/?tab=${exitTab}`);
          }}
        >
          Exit
        </Button>

        {/* QA skips go straight through with a fixed reason, no modal. */}
        <CoverageBetaSkipBtn
          taskDetailResp={taskDetailResp}
          qaMode={qaMode || qaEdit}
        />

        {submitActions}
      </div>
    </div>
  );
};
